import { notification } from 'antd';
import { data_from_server_post, get_base_url } from '@/shared/http/client';
import { isInstancesCapError, triggerModelDownload, triggerBundledModelDownload } from '@/features/trainings/utils/modelDownload';
import { DEFAULT_CLIENT_ID } from '@/config/client';
import { sanitizeRuntimeConfigForApi } from '@/features/deployments/components/configureDeployment/taskRuntimeDefaults';

function resolveClientId(training) {
  return training?.client_id || DEFAULT_CLIENT_ID;
}

/**
 * Start packaged model download (progress via GlobalContext `setProgressStatus`).
 *
 * Accepts both the legacy camelCase shape (``artifactType`` /
 * ``instanceGroupCount`` / ``device``) and the snake_case shape the
 * Configure Deployment modal emits (``cloud_provider`` /
 * ``hardware_device`` / ``accelerator`` / ``instance_type`` /
 * ``instance_group_count`` / ``device`` / ``long_input_processing``
 * / ``prediction_strategy`` / ``preferred_infrastructure``) so the
 * ZIP path and the new Docker path can share the same helper.
 *
 * @param {object} training — row / drawer `version` with client_id, llm_usecase_id, version
 * @param {import('react').Dispatch<unknown>} setProgressStatus
 * @param {object} [callbacks] — ``onSuccess``, ``onInstancesCapError``, ``onError``
 */
export function modelDrawerDownloadModel(
  training,
  setProgressStatus,
  downloadOptions = {},
  callbacks = {}
) {
  if (!training || training.version == null) {
    notification.error({
      message: 'Download Failed',
      description: 'No version available for download',
      duration: 4,
    });
    return;
  }

  const runtimeConfig = sanitizeRuntimeConfigForApi(training, downloadOptions);

  triggerModelDownload({
    clientId: resolveClientId(training),
    usecaseId: training.llm_usecase_id,
    version: training.version,
    setProgressStatus,
    artifactType: runtimeConfig.artifactType || downloadOptions.artifactType || 'zip',
    instanceGroupCount:
      runtimeConfig.instance_group_count ??
      downloadOptions.instance_group_count ??
      downloadOptions.instanceGroupCount ??
      2,
    device: runtimeConfig.device || downloadOptions.device || 'cpu',
    cloudProvider: runtimeConfig.cloud_provider ?? downloadOptions.cloud_provider ?? null,
    hardwareDevice: runtimeConfig.hardware_device ?? downloadOptions.hardware_device ?? null,
    accelerator: runtimeConfig.accelerator ?? downloadOptions.accelerator ?? null,
    instanceType: runtimeConfig.instance_type ?? downloadOptions.instance_type ?? null,
    longInputProcessing:
      runtimeConfig.long_input_processing ??
      downloadOptions.long_input_processing ??
      null,
    predictionStrategy: runtimeConfig.prediction_strategy,
    preferredInfrastructure: Boolean(downloadOptions.preferred_infrastructure),
    outputFilename: downloadOptions.output_filename ?? null,
    ecrRepositoryUri: downloadOptions.ecr_repository_uri ?? null,
    ecrAccountMode: downloadOptions.ecr_account_mode ?? null,
    ecrAlias: downloadOptions.ecr_alias ?? null,
    gcrRepositoryUri: downloadOptions.gcr_repository_uri ?? null,
    gcrAuthMode: downloadOptions.gcr_auth_mode ?? null,
    gcrAlias: downloadOptions.gcr_alias ?? null,
    dockerhubRepository: downloadOptions.dockerhub_repository ?? null,
    dockerhubAlias: downloadOptions.dockerhub_alias ?? null,
    forcePush: Boolean(downloadOptions.force_push),
    suppressFreeUsageGate: Boolean(downloadOptions.suppressFreeUsageGate),
    onSuccess: callbacks.onSuccess,
    onInstancesCapError: callbacks.onInstancesCapError,
    onError: callbacks.onError,
    onDuplicatePush: callbacks.onDuplicatePush,
  });
}

/**
 * Start a bundled multi-model download (multiple tasks → one artifact).
 *
 * ``artifactType: 'docker'`` builds one Triton image / tar; ``'zip'`` packages
 * all selected models' raw files into a single ZIP. Defaults to ``'docker'``
 * for backward compatibility.
 */
export function modelDrawerDownloadBundledModels(
  tasks,
  setProgressStatus,
  sharedOptions = {},
  callbacks = {}
) {
  if (!tasks?.length) {
    notification.error({
      message: 'Download Failed',
      description: 'No tasks selected for download',
      duration: 4,
    });
    return;
  }

  const primary = tasks[0].training;
  const clientId = resolveClientId(primary);
  const artifactType = sharedOptions.artifactType || 'docker';

  triggerBundledModelDownload({
    clientId,
    bundledTasks: tasks.map(({ training, config }) => {
      const merged = { ...sharedOptions, ...config };
      const runtimeConfig = sanitizeRuntimeConfigForApi(training, merged);
      return {
        llm_usecase_id: training.llm_usecase_id,
        deployed_version: training.version,
        instance_group_count:
          runtimeConfig.instance_group_count ?? sharedOptions.instance_group_count ?? 2,
        long_input_processing: runtimeConfig.long_input_processing ?? null,
        prediction_strategy: runtimeConfig.prediction_strategy ?? null,
      };
    }),
    setProgressStatus,
    artifactType,
    instanceGroupCount:
      sharedOptions.instance_group_count ?? sharedOptions.instanceGroupCount ?? 2,
    device: sharedOptions.device || 'cpu',
    cloudProvider: sharedOptions.cloud_provider ?? null,
    hardwareDevice: sharedOptions.hardware_device ?? null,
    accelerator: sharedOptions.accelerator ?? null,
    instanceType: sharedOptions.instance_type ?? null,
    preferredInfrastructure: Boolean(sharedOptions.preferred_infrastructure),
    outputFilename:
      sharedOptions.output_filename ?? tasks[0]?.config?.output_filename ?? null,
    suppressFreeUsageGate: Boolean(sharedOptions.suppressFreeUsageGate),
    onSuccess: callbacks.onSuccess,
    onInstancesCapError: callbacks.onInstancesCapError,
    onError: callbacks.onError,
  });
}

/**
 * Download benchmark JSON for a distillation / training event.
 * @param {object} training — must include distillation_event_id; usecase_name used for filename
 */
export async function modelDrawerDownloadBenchmark(training) {
  const clientId = resolveClientId(training);
  const distillationEventId = training.distillation_event_id;
  if (!distillationEventId) {
    notification.error({
      message: 'Download Failed',
      description: 'No benchmark version available for download',
      duration: 4,
    });
    return;
  }

  const origin = window.location.origin;

  try {
    const url = `${get_base_url(origin)}/download-benchmark-version`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        client_id: clientId,
        distillation_event_id: distillationEventId,
        format: 'json',
      }),
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: response.statusText }));
      notification.error({
        message: 'Download Failed',
        description: errorData?.detail || errorData?.message || 'Failed to download benchmark',
        duration: 6,
      });
      return;
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    const versionLabel = training.version != null ? training.version : 'unknown';
    const usecaseLabel = training.usecase_name || 'model';
    link.download = `${usecaseLabel}_benchmark_v${versionLabel}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error('Error downloading benchmark:', error);
    notification.error({
      message: 'Download Failed',
      description: error.message || 'Failed to download benchmark',
      duration: 6,
    });
  }
}

/**
 * @param {object} training
 * @param {object} [deploymentConfig] — output of the Configure Deployment
 *   modal. ``null``/empty preserves today's behavior (server uses its
 *   defaults).
 * @param {() => void} [onSuccess] — optimistic UI or refetch (e.g. refresh usecases / trainings)
 * @param {object} [callbacks] — ``onInstancesCapError``, ``onError`` for configure-modal UX
 */
export function modelDrawerDeploy(training, deploymentConfig, onSuccess, callbacks = {}) {
  // Backward-compat: callers used to pass `(training, onSuccess)` so
  // promote the function arg to `onSuccess` when the middle slot is a
  // function instead of a config object.
  let resolvedConfig = deploymentConfig;
  let resolvedOnSuccess = onSuccess;
  if (typeof deploymentConfig === 'function') {
    resolvedConfig = null;
    resolvedOnSuccess = deploymentConfig;
  }

  const payload = {
    client_id: resolveClientId(training),
    llm_usecase_id: training.llm_usecase_id,
    task_type: training.task_type,
    request_type: 'deploy',
    deployed_version: training.version,
  };
  if (resolvedConfig) {
    const runtimeConfig = sanitizeRuntimeConfigForApi(training, resolvedConfig);
    payload.cloud_provider = runtimeConfig.cloud_provider ?? null;
    payload.hardware_device = runtimeConfig.hardware_device ?? null;
    payload.accelerator = runtimeConfig.accelerator ?? null;
    payload.instance_type = runtimeConfig.instance_type ?? null;
    payload.instance_group_count = runtimeConfig.instance_group_count ?? null;
    payload.device = runtimeConfig.device ?? null;
    payload.long_input_processing = runtimeConfig.long_input_processing ?? null;
    payload.prediction_strategy = runtimeConfig.prediction_strategy ?? null;
    payload.preferred_infrastructure = Boolean(
      resolvedConfig.preferred_infrastructure
    );
  }

  const handleDeployFailure = (message, failureLabel = 'Deployment Failed') => {
    const detail = message || `Failed to start model ${resolvedConfig ? 'deployment' : 'undeployment'}`;
    if (isInstancesCapError(detail) && callbacks.onInstancesCapError) {
      callbacks.onInstancesCapError(detail);
      return;
    }
    if (callbacks.onError) {
      callbacks.onError(detail);
      return;
    }
    notification.error({
      message: failureLabel,
      description: detail,
      duration: 6,
    });
  };

  data_from_server_post(
    'model-deployment',
    payload,
    (result) => {
      if (result.success) {
        const storageKey = `deploying_${training.llm_usecase_id}`;
        localStorage.setItem(storageKey, String(training.version));
        // No success toast: the triggering button shows a loading spinner
        // until the deployment status flips, which is the intended feedback.
        resolvedOnSuccess?.(training);
      } else {
        handleDeployFailure(result.message || 'Failed to start model deployment');
      }
    },
    (error) => {
      handleDeployFailure(error);
    }
  );
}

/**
 * @param {object} training
 * @param {(t: object, versionToUndeploy: number|string) => void} [onSuccess]
 * @returns {Promise<boolean>} resolves once the server answered (true when the
 *   undeploy request was accepted) — lets callers show a loading state on the
 *   triggering button until the request settles.
 */
export function modelDrawerUndeploy(training, onSuccess) {
  const versionToUndeploy = training.deployed_version || training.version;
  return new Promise((resolve) => {
    data_from_server_post(
      'model-deployment',
      {
        client_id: resolveClientId(training),
        llm_usecase_id: training.llm_usecase_id,
        task_type: training.task_type,
        request_type: 'undeploy',
        deployed_version: versionToUndeploy,
      },
      (result) => {
        if (result.success) {
          const storageKey = `undeploying_${training.llm_usecase_id}`;
          localStorage.setItem(storageKey, String(versionToUndeploy));
          // No success toast: the triggering button shows a loading spinner
          // until the undeploy status flips, which is the intended feedback.
          onSuccess?.(training, versionToUndeploy);
        } else {
          notification.error({
            message: 'Undeployment Failed',
            description: result.message || 'Failed to start model undeployment',
            duration: 6,
          });
        }
        resolve(Boolean(result.success));
      },
      (error) => {
        notification.error({
          message: 'Undeployment Failed',
          description:
            (typeof error === 'string' ? error : error?.message) ||
            'Failed to start model undeployment',
          duration: 6,
        });
        resolve(false);
      }
    );
  });
}
